// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Anchor, Button, Card, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { Appointment, AppointmentParticipant, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { AppointmentTimeWithTimezone } from '../../../components/appointments/AppointmentTimeWithTimezone';
import { PageHeader } from '../../../components/layout/PageHeader';
import { BookingConfirmation } from '../../../components/patient-appointments/BookingConfirmation';
import { SlotPicker } from '../../../components/patient-appointments/SlotPicker';
import { useAppointmentTypes } from '../../../hooks/useAppointmentTypes';
import type { ProviderAppointmentCandidate } from '../../../scheduling-next/types';
import { useAvailableAppointments } from '../../../scheduling-next/useAvailableAppointments';
import { rescheduleProviderV2Appointment } from '../../../services/appointment-lifecycle-service';
import { formatAppointmentDate } from '../../../utils/appointment-formatting';

/**
 * Provider-initiated reschedule page, nested under
 * `/Patient/:patientId/Appointment/reschedule/:appointmentId`.
 *
 * Loads the existing Appointment, pre-selects its appointment type, and shows
 * a SlotPicker constrained to the same type. On confirmation, calls
 * `rescheduleAppointment` which atomically claims the new slot, creates a
 * replacement Appointment + Encounter, and cancels the old one.
 *
 * Copy is third-person (clinician viewpoint). The route is gated by
 * `schedule-appointment` permission in App.tsx.
 * @returns The reschedule page component
 */
export function ReschedulePage(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { patientId, appointmentId } = useParams<{ patientId: string; appointmentId: string }>();
  const { appointmentTypes, loading: typesLoading } = useAppointmentTypes();

  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const [patient, setPatient] = useState<Patient | undefined>();
  const [appointmentType, setAppointmentType] = useState<AppointmentTypeDefinition | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  const [selectedCandidate, setSelectedCandidate] = useState<ProviderAppointmentCandidate | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const appointmentsHref = `/Patient/${patientId}/Appointment`;

  const backToAppointments = useCallback((): void => {
    navigate(appointmentsHref);
  }, [navigate, appointmentsHref]);

  const loadAppointment = useCallback(async () => {
    if (!appointmentId) {
      setError('No appointment ID provided.');
      setLoading(false);
      return;
    }

    try {
      const appt = await medplum.readResource('Appointment', appointmentId);
      if (appt.status !== 'booked') {
        setError('This appointment cannot be rescheduled because it is not currently booked.');
        setLoading(false);
        return;
      }
      setAppointment(appt);
      const patientReference = appt.participant?.find((item: AppointmentParticipant) =>
        item.actor?.reference?.startsWith('Patient/')
      )?.actor;
      if (patientReference?.reference?.startsWith('Patient/')) {
        try {
          setPatient(await medplum.readReference(patientReference as Reference<Patient>));
        } catch {
          // Legacy rescheduling does not need the patient resource. V2 booking
          // will surface a clear error if its required patient cannot be read.
        }
      }
    } catch {
      setError('Appointment not found.');
    } finally {
      setLoading(false);
    }
  }, [medplum, appointmentId]);

  useEffect(() => {
    loadAppointment().catch(console.error);
  }, [loadAppointment]);

  useEffect(() => {
    if (!appointment || typesLoading) {
      return;
    }
    const typeCode = appointment.appointmentType?.coding?.[0]?.code;
    if (typeCode) {
      const found = appointmentTypes.find((t) => t.coding.code === typeCode);
      setAppointmentType(found);
    }
  }, [appointment, appointmentTypes, typesLoading]);

  async function handleConfirm(): Promise<void> {
    if (!appointmentId || !selectedCandidate || !patient || !appointmentType) {
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      await rescheduleProviderV2Appointment(medplum, {
        appointmentId,
        patient,
        candidate: selectedCandidate,
        appointmentType,
      });

      setShowConfirmation(false);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while rescheduling.');
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  const breadcrumb = (
    <Anchor component={Link} to={appointmentsHref} size="sm">
      Appointments &gt;
    </Anchor>
  );

  if (loading || typesLoading) {
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Reschedule Appointment" />
        </Paper>
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Stack align="center" py="xl">
            <Loader />
            <Text c="dimmed">Loading appointment…</Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (error && !selectedCandidate) {
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Reschedule Appointment" />
        </Paper>
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Alert color="red">{error}</Alert>
        </Paper>
      </Stack>
    );
  }

  if (success && appointmentType && selectedCandidate) {
    const selectedStart = selectedCandidate.appointment.start ?? '';
    const selectedEnd = selectedCandidate.appointment.end ?? '';
    const selectedProvider = selectedCandidate.providerName;
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Reschedule Appointment" />
        </Paper>
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Stack gap="md" align="center" py="xl">
            <IconCalendarCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Appointment Rescheduled!</Title>
            <Card withBorder p="lg" radius="md" maw={400} w="100%">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Type
                  </Text>
                  <Text size="sm">{appointmentType.display}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    New Date
                  </Text>
                  <Text size="sm">{formatAppointmentDate(selectedStart)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    New Time
                  </Text>
                  <AppointmentTimeWithTimezone start={selectedStart} end={selectedEnd} textProps={{ size: 'sm' }} />
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Provider
                  </Text>
                  <Text size="sm">{selectedProvider}</Text>
                </Group>
              </Stack>
            </Card>
            <Text size="sm" c="dimmed" ta="center">
              The patient's previous appointment has been cancelled. They will receive a new email confirmation with the
              updated time.
            </Text>
            <Button variant="outline" onClick={backToAppointments}>
              Back to Appointments
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (appointment && appointmentType) {
    return (
      <Stack gap="lg" p="md">
        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          {breadcrumb}
          <PageHeader title="Reschedule Appointment" />
        </Paper>

        <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
          <Card withBorder p="md" radius="md" mb="lg">
            <Text size="sm" fw={500} mb="xs">
              Current Appointment
            </Text>
            <Group gap="lg">
              <Text size="sm" c="dimmed">
                {appointmentType.display}
              </Text>
              <Text size="sm" c="dimmed">
                {formatAppointmentDate(appointment.start ?? '')}
              </Text>
              <AppointmentTimeWithTimezone
                start={appointment.start}
                end={appointment.end}
                textProps={{ size: 'sm', c: 'dimmed' }}
              />
            </Group>
          </Card>

          {error && (
            <Alert color="red" title="Reschedule failed" mb="md">
              {error}
            </Alert>
          )}

          <SlotPicker
            engine="medplum-v2"
            appointmentType={appointmentType}
            selectedSlot={selectedCandidate}
            onSlotSelected={setSelectedCandidate}
          />

          <Group justify="center" mt="md">
            <Button variant="outline" onClick={backToAppointments}>
              Cancel
            </Button>
            <Button disabled={!selectedCandidate} onClick={() => setShowConfirmation(true)}>
              Reschedule Appointment
            </Button>
          </Group>

          {appointmentType && selectedCandidate && (
            <BookingConfirmation
              opened={showConfirmation}
              appointmentType={appointmentType}
              slot={selectedCandidate}
              onConfirm={handleConfirm}
              onBack={() => setShowConfirmation(false)}
              isSubmitting={isSubmitting}
            />
          )}
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <Paper withBorder radius="md" shadow="sm" p="lg" bg="white">
        <Text c="dimmed">Unable to load appointment details.</Text>
      </Paper>
    </Stack>
  );
}
